import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getReviewProperties, getAllSellers } from '../../Redux/Actions/actions';
import { propertyTypeOptions, currencyOptions } from '../PropertyPreviewModal/propertyLabels';
import CardAprobar from '../CardAprobar/CardAprobar';
import style from './CardAprobarContainer.module.css';

const TABS = [
  { key: 'sin-doc', label: 'Documentación pendiente', countKey: 'sinDoc', dot: '🔴' },
  { key: 'solo-doc', label: 'Pendiente de alta', countKey: 'soloDoc', dot: '🟡' },
  { key: 'alta', label: 'Publicadas', countKey: 'alta', dot: '🟢' },
  { key: 'todas', label: 'Todas', countKey: 'total', dot: '📋' },
];

const ORDER_OPTIONS = [
  { value: 'updatedAt', label: 'Última actualización' },
  { value: 'createdAt', label: 'Más recientes' },
  { value: 'price', label: 'Precio ↑' },
  { value: 'priceDesc', label: 'Precio ↓' },
];

const CardAprobarContainer = () => {
  const dispatch = useDispatch();
  const reviewProperties = useSelector((state) => state.reviewProperties);
  const reviewCounts = useSelector((state) => state.reviewCounts);
  const reviewPagination = useSelector((state) => state.reviewPagination);
  const reviewLoading = useSelector((state) => state.reviewLoading);
  const sellers = useSelector((state) => state.sellers);

  const [estado, setEstado] = useState('sin-doc');
  const [search, setSearch] = useState('');
  const [operacion, setOperacion] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [orderBy, setOrderBy] = useState('updatedAt');
  const [page, setPage] = useState(1);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalog, setCatalog] = useState({
    propertyType: '',
    currency: '',
    minPrice: '',
    maxPrice: '',
    environments: '',
    rooms: '',
    bathrooms: '',
    garages: '',
  });

  useEffect(() => {
    dispatch(getAllSellers());
  }, [dispatch]);

  const params = useMemo(() => {
    const base = { estado, orderBy, page, limit: 20 };
    if (search.trim()) base.search = search.trim();
    if (operacion) base.operacion = operacion;
    if (sellerId) base.sellerId = sellerId;
    Object.entries(catalog).forEach(([key, value]) => {
      if (String(value).trim() !== '') base[key] = value;
    });
    return base;
  }, [estado, orderBy, page, search, operacion, sellerId, catalog]);

  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(getReviewProperties(params)).catch(() => {});
    }, 300);
    return () => clearTimeout(delay);
  }, [dispatch, params]);

  const refresh = useCallback(() => {
    dispatch(getReviewProperties(params)).catch(() => {});
  }, [dispatch, params]);

  const handleTabChange = (key) => {
    setEstado(key);
    setPage(1);
  };

  const handleCatalogChange = (field, value) => {
    setCatalog((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setOperacion('');
    setSellerId('');
    setOrderBy('updatedAt');
    setCatalog({
      propertyType: '', currency: '', minPrice: '', maxPrice: '',
      environments: '', rooms: '', bathrooms: '', garages: '',
    });
    setPage(1);
  };

  const totalPages = reviewPagination?.totalPages || 1;
  const sellerOptions = Array.isArray(sellers) ? sellers : [];

  return (
    <div className={style.container}>
      {/* Pestañas por bucket */}
      <div className={style.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${style.tab} ${estado === tab.key ? style.tabActive : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <span>{tab.dot} {tab.label}</span>
            {reviewCounts && (
              <span className={style.tabCount}>{reviewCounts[tab.countKey] ?? 0}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filtros principales */}
      <div className={style.filters}>
        <input
          type="text"
          placeholder="Buscar por ID, título o calle..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className={style.searchInput}
        />
        <select
          value={operacion}
          onChange={(e) => { setOperacion(e.target.value); setPage(1); }}
          className={style.select}
        >
          <option value="">Toda operación</option>
          <option value="venta">Venta</option>
          <option value="alquiler">Alquiler</option>
        </select>
        <select
          value={sellerId}
          onChange={(e) => { setSellerId(e.target.value); setPage(1); }}
          className={style.select}
        >
          <option value="">Todos los agentes</option>
          {sellerOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {[s.name, s.last_name].filter(Boolean).join(' ') || s.mail}
            </option>
          ))}
        </select>
        <select
          value={orderBy}
          onChange={(e) => { setOrderBy(e.target.value); setPage(1); }}
          className={style.select}
        >
          {ORDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          className={style.toggleCatalog}
          onClick={() => setShowCatalog((v) => !v)}
        >
          {showCatalog ? 'Menos filtros' : 'Más filtros'}
        </button>
        <button className={style.clearBtn} onClick={clearFilters}>
          Limpiar
        </button>
      </div>

      {/* Filtros de catálogo (colapsable) */}
      {showCatalog && (
        <div className={style.catalogFilters}>
          <select
            value={catalog.propertyType}
            onChange={(e) => handleCatalogChange('propertyType', e.target.value)}
            className={style.select}
          >
            <option value="">Tipo de propiedad</option>
            {propertyTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={catalog.currency}
            onChange={(e) => handleCatalogChange('currency', e.target.value)}
            className={style.select}
          >
            <option value="">Moneda</option>
            {currencyOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Precio mín."
            value={catalog.minPrice}
            onChange={(e) => handleCatalogChange('minPrice', e.target.value)}
            className={style.numInput}
          />
          <input
            type="number"
            placeholder="Precio máx."
            value={catalog.maxPrice}
            onChange={(e) => handleCatalogChange('maxPrice', e.target.value)}
            className={style.numInput}
          />
          <input
            type="number"
            placeholder="Ambientes"
            value={catalog.environments}
            onChange={(e) => handleCatalogChange('environments', e.target.value)}
            className={style.numInput}
          />
          <input
            type="number"
            placeholder="Dormitorios"
            value={catalog.rooms}
            onChange={(e) => handleCatalogChange('rooms', e.target.value)}
            className={style.numInput}
          />
          <input
            type="number"
            placeholder="Baños"
            value={catalog.bathrooms}
            onChange={(e) => handleCatalogChange('bathrooms', e.target.value)}
            className={style.numInput}
          />
          <input
            type="number"
            placeholder="Cocheras"
            value={catalog.garages}
            onChange={(e) => handleCatalogChange('garages', e.target.value)}
            className={style.numInput}
          />
        </div>
      )}

      {/* Resultado */}
      <div className={style.resultInfo}>
        {reviewLoading
          ? 'Cargando propiedades...'
          : `${reviewPagination?.totalItems ?? reviewProperties.length} propiedad(es)`}
      </div>

      {!reviewLoading && reviewProperties.length === 0 ? (
        <div className={style.empty}>No hay propiedades para este filtro.</div>
      ) : (
        <div className="card-deck">
          {reviewProperties.map((property) => (
            <CardAprobar key={property.id} property={property} onRefresh={refresh} />
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className={style.pagination}>
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={style.pageBtn}
          >
            ← Anterior
          </button>
          <span className={style.pageInfo}>Página {page} de {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={style.pageBtn}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default CardAprobarContainer;
